import { $, el, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { fillForm, formToObject } from '../lib/form.js';
import { mountResourceEditor } from '../lib/resource-editor.js';
import { mountResourceList } from '../lib/resource-list.js';
import { findSelectMenu } from '../lib/select-menu.js';

/**
 * Recruitment forms.
 *
 * The questions are built in the browser and read back at save time rather than
 * held in a variable: the save has to serialise what is on screen at that
 * instant, and a copy taken at mount time would store the questions the page
 * opened with.
 */

interface Question {
  label: string;
  style: string;
  placeholder: string;
  required: boolean;
  maxLength: number;
}

interface Form {
  _id: string;
  name: string;
  questions: Question[];
  channelId: string;
  reviewChannelId: string;
  roleId: string;
  open: boolean;
  reapplyAfterRefusal: boolean;
  dmOnDecision: boolean;
  panel: Record<string, string>;
}

/** Discord's ceiling for a modal window, and therefore for this form. */
const MAX_QUESTIONS = 5;

/** The list: all it keeps is republishing. */
export function initApplicationsListPage(): void {
  if (!document.querySelector('#form-list')) return;

  const { guildId } = pageData<{ guildId: string }>();

  mountResourceList({
    endpoint: 'applications',
    prefix: 'form',
    guildId,
    listSelector: '#form-list',
    successMessage: ct('formSaved', 'Form saved and published.'),
  });
}

export function initApplicationsEditorPage(): void {
  const editor = $<HTMLFormElement>('[data-form-editor]');
  if (!editor) return;

  const { form, guildId } = pageData<{ form: Form; guildId: string }>();

  fillForm(editor, form as unknown as Record<string, unknown>);

  /*
   * The dropdowns do not fill in that way.
   *
   * `upgradeSelects` swaps each `<select>` for a component that leaves a hidden
   * field behind it: `fillForm` writes the value there, but the display keeps
   * the first option — an existing form would open on the wrong channel, and
   * the page would look empty while being full.
   */
  for (const [field, value] of [
    ['channelId', form.channelId],
    ['reviewChannelId', form.reviewChannelId],
    ['roleId', form.roleId],
    ['panel.buttonStyle', form.panel.buttonStyle],
  ] as const) {
    const component = findSelectMenu(editor, field);
    if (component) component.value = value ? [value] : [];
  }

  /*
   * Three switches ticked by default on a creation.
   *
   * A form somebody has just described should collect applications and answer
   * their candidates without anybody having to think of it. `fillForm` cannot
   * guess it: the document does not exist yet.
   */
  if (!form._id) {
    for (const name of ['open', 'dmOnDecision']) {
      const field = editor.elements.namedItem(name);
      if (field instanceof HTMLInputElement) field.checked = true;
    }
  }

  const questions = mountQuestions(editor, form.questions);

  mountResourceEditor({
    endpoint: 'applications',
    listUrl: `/modules/applications?guildId=${guildId}`,
    guildId,
    form: editor,
    itemId: form._id,

    serialize: () => ({
      ...formToObject(editor),
      channelId: findSelectMenu(editor, 'channelId')?.single ?? '',
      reviewChannelId: findSelectMenu(editor, 'reviewChannelId')?.single ?? '',
      roleId: findSelectMenu(editor, 'roleId')?.single ?? '',
      questions: questions(),
    }),

    labels: {
      saved: ct('formSaved', 'Form saved and published.'),
      confirmDelete: ct(
        'confirmDeleteForm',
        'Delete this form? The applications it received are kept.',
      ),
      deleted: ct('formDeleted', 'Form deleted.'),
    },
  });
}

/** The questions, built in the browser and read back on save. */
function mountQuestions(form: HTMLFormElement, initial: Question[]): () => Question[] {
  const host = $<HTMLElement>('[data-questions]', form);
  const add = $<HTMLButtonElement>('[data-add-question]', form);
  if (!host || !add) return () => [];

  const draw = () => {
    // Five is Discord's ceiling for a modal, not a product choice: past it the
    // whole window is refused, and the button would open nothing at all.
    add.hidden = host.children.length >= MAX_QUESTIONS;
  };

  const row = (question: Question) => {
    const line = el('div', { class: 'flex flex-wrap items-center gap-2' });

    const label = el('input', {
      class: 'input input-bordered input-sm flex-1',
      attrs: { type: 'text', maxlength: '45', 'data-question-label': '' },
    });
    label.value = question.label;

    const style = el('select', {
      class: 'select select-bordered select-sm w-40',
      attrs: { 'data-question-style': '' },
    });

    for (const [value, text] of [
      ['short', ct('questionStyleShort', 'One line')],
      ['paragraph', ct('questionStyleParagraph', 'Paragraph')],
    ] as const) {
      const option = el('option', { text });
      option.value = value;
      style.append(option);
    }
    style.value = question.style;

    const required = el('input', {
      attrs: { type: 'checkbox', 'data-question-required': '', class: 'toggle toggle-sm' },
    });
    required.checked = question.required;

    const remove = el('button', {
      class: 'btn btn-ghost btn-sm',
      text: ct('removeQuestion', 'Remove'),
      attrs: { type: 'button' },
    });
    remove.addEventListener('click', () => {
      line.remove();
      draw();
    });

    line.append(label, style, required, remove);
    return line;
  };

  for (const question of initial.slice(0, MAX_QUESTIONS)) host.append(row(question));
  draw();

  add.addEventListener('click', () => {
    host.append(
      row({ label: '', style: 'short', placeholder: '', required: true, maxLength: 300 }),
    );
    draw();
  });

  return () =>
    [...host.children].flatMap((line) => {
      const label = $<HTMLInputElement>('[data-question-label]', line)?.value.trim() ?? '';
      // A question with no wording would be a nameless field in the
      // candidate's window; dropping it beats storing it and asking nothing.
      if (!label) return [];

      return [
        {
          label,
          style: $<HTMLSelectElement>('[data-question-style]', line)?.value ?? 'short',
          placeholder: '',
          required: $<HTMLInputElement>('[data-question-required]', line)?.checked ?? true,
          maxLength: 300,
        },
      ];
    });
}
